import React, {useEffect, useState} from 'react';
import type {
  HangfireJobSummary,
  HangfireRecurringJobResponse,
} from 'src/api/ProactApiClient';
import {HangfireJobState} from 'src/api/ProactApiClient';
import {
  useDeleteHangfireJobMutation,
  useGetHangfireJobDetailQuery,
  useGetHangfireJobsQuery,
  useGetHangfireOverviewQuery,
  useGetHangfireRecurringJobsQuery,
  useGetHangfireServersQuery,
  useRequeueHangfireJobMutation,
  useTriggerHangfireRecurringJobMutation,
} from 'src/redux/queries/backgroundJob';
import {StatCard} from 'src/design/molecules/StatCard';
import {
  CheckIcon,
  ClockIcon,
  CloseIcon,
  IconSize,
  PlayIcon,
  WarningIcon,
} from 'src/design/atoms/icons/CommonIcons';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

import {
  Badge,
  Button,
  Drawer,
  Menu,
  Tabs,
  Timeline,
} from '@mantine/core';
import {Table} from 'src/design/atoms/Table';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Code} from 'src/design/atoms/Code';
import {Collapse} from 'src/design/atoms/Collapse';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {AreaChart} from '@mantine/charts';
import {
  IconCircleDot,
  IconListDetails,
  IconRefresh,
  IconRepeat,
  IconServer,
  IconTrash,
} from '@tabler/icons-react';

const POLL_INTERVAL_MS = 10_000;
const PAGE_SIZE = 25;

interface HangfireJobStateInfo {
  label: string;
  color: string;
}

const JOB_STATE_INFO: Record<HangfireJobState, HangfireJobStateInfo> = {
  [HangfireJobState.Enqueued]: {label: 'Enqueued', color: 'blue'},
  [HangfireJobState.Processing]: {label: 'Processing', color: 'cyan'},
  [HangfireJobState.Scheduled]: {label: 'Scheduled', color: 'orange'},
  [HangfireJobState.Succeeded]: {label: 'Succeeded', color: 'green'},
  [HangfireJobState.Failed]: {label: 'Failed', color: 'red'},
  [HangfireJobState.Deleted]: {label: 'Deleted', color: 'gray'},
};

const hangfireStateColor = (state: string | undefined): string => {
  if (!state) return 'gray';
  const lower = state.toLowerCase();
  if (lower === 'enqueued') return 'blue';
  if (lower === 'processing') return 'cyan';
  if (lower === 'scheduled') return 'orange';
  if (lower === 'succeeded') return 'green';
  if (lower === 'failed') return 'red';
  if (lower === 'deleted') return 'gray';
  return 'gray';
};

const formatRelative = (date: Date | undefined): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date as unknown as string);
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString();
};

const formatDate = (date: Date | undefined): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date as unknown as string);
  return d.toLocaleString();
};

const tryParseJson = (val: string | undefined): string => {
  if (!val) return '';
  try {
    return JSON.stringify(JSON.parse(val), null, 2);
  } catch {
    return val;
  }
};

export const HangfireMonitorPage: React.FC = () => {
  useSetDocumentTitle('Hangfire Monitor');
  const {setAlert} = useAlert();

  const [activeTab, setActiveTab] = useState<HangfireJobState>(
    HangfireJobState.Enqueued
  );
  const [jobsPage, setJobsPage] = useState(1);

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [exceptionExpanded, setExceptionExpanded] = useState(false);

  const [triggeringId, setTriggeringId] = useState<string | null>(null);
  const [mutatingJobId, setMutatingJobId] = useState<string | null>(null);

  const {
    data: overview,
    isLoading: overviewLoading,
    refetch: refetchOverview,
  } = useGetHangfireOverviewQuery(undefined, {
    pollingInterval: POLL_INTERVAL_MS,
  });
  const {
    data: servers = [],
    isLoading: serversLoading,
    refetch: refetchServers,
  } = useGetHangfireServersQuery(undefined, {
    pollingInterval: POLL_INTERVAL_MS,
  });
  const {
    data: jobs,
    isFetching: jobsLoading,
    error: jobsError,
    refetch: refetchJobs,
  } = useGetHangfireJobsQuery({
    state: activeTab,
    page: jobsPage,
    size: PAGE_SIZE,
  });
  const {
    data: recurring = [],
    isLoading: recurringLoading,
    error: recurringError,
    refetch: refetchRecurring,
  } = useGetHangfireRecurringJobsQuery();
  const {
    data: jobDetail,
    isFetching: detailLoading,
    error: detailError,
  } = useGetHangfireJobDetailQuery(selectedJobId ?? '', {
    skip: selectedJobId === null,
  });

  const [requeueJob] = useRequeueHangfireJobMutation();
  const [deleteJob] = useDeleteHangfireJobMutation();
  const [triggerRecurringJob] = useTriggerHangfireRecurringJobMutation();

  const loadError = jobsError ?? recurringError ?? detailError;

  useEffect(() => {
    if (loadError) setAlert(parseErrorMessage(loadError), 'error');
  }, [loadError, setAlert]);

  const refreshAll = (): void => {
    void refetchOverview();
    void refetchServers();
    void refetchJobs();
    void refetchRecurring();
  };

  const handleTabChange = (state: HangfireJobState): void => {
    setActiveTab(state);
    setJobsPage(1);
  };

  const openJobDetail = (id: string): void => {
    setSelectedJobId(id);
    setDrawerOpen(true);
    setExceptionExpanded(false);
  };

  const handleRequeue = async (id: string) => {
    setMutatingJobId(id);
    try {
      await requeueJob(id).unwrap();
      setAlert(`Job ${id} requeued.`, 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setMutatingJobId(null);
  };

  const handleDeleteJob = async (id: string) => {
    setMutatingJobId(id);
    try {
      await deleteJob(id).unwrap();
      setAlert(`Job ${id} deleted.`, 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setMutatingJobId(null);
  };

  const handleTrigger = async (id: string) => {
    setTriggeringId(id);
    try {
      await triggerRecurringJob(id).unwrap();
      setAlert(`Recurring job "${id}" triggered.`, 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setTriggeringId(null);
  };

  const buildChartData = () => {
    if (!overview) return [];
    const succeededMap = overview.hourlySucceeded ?? {};
    const failedMap = overview.hourlyFailed ?? {};
    const keys = Array.from(
      new Set([...Object.keys(succeededMap), ...Object.keys(failedMap)])
    ).sort();
    return keys.map(k => ({
      hour: new Date(k).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      Succeeded: succeededMap[k] ?? 0,
      Failed: failedMap[k] ?? 0,
    }));
  };

  const canRequeue = (state: HangfireJobState) =>
    state === HangfireJobState.Failed || state === HangfireJobState.Deleted;

  const canDelete = (state: HangfireJobState) =>
    state === HangfireJobState.Enqueued ||
    state === HangfireJobState.Processing ||
    state === HangfireJobState.Scheduled ||
    state === HangfireJobState.Failed;

  const jobTimestamp = (job: HangfireJobSummary): string => {
    const d =
      job.enqueuedAt ??
      job.startedAt ??
      job.succeededAt ??
      job.failedAt ??
      job.deletedAt ??
      job.scheduledAt;
    return formatRelative(d);
  };

  const chartData = buildChartData();

  return (
    <ContentWrapper>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.AdminSystem()}
          breadcrumbs={[{label: 'Hangfire Monitor'}]}
        />

        <PageHeader>
          <PageHeader.Title
            icon={<IconServer size={IconSize.md} />}
            subtitle="Live view of Hangfire queue depths, servers, and job history">
            Hangfire Monitor
          </PageHeader.Title>
          <PageHeader.Actions>
            <Button
              size="sm"
              variant="subtle"
              color="gray"
              leftSection={<IconRefresh size={16} />}
              onClick={refreshAll}>
              Refresh
            </Button>
          </PageHeader.Actions>
        </PageHeader>

        {/* Overview stats */}
        {overviewLoading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : overview ? (
          <SimpleGrid cols={{base: 2, sm: 3, md: 6}} spacing="md">
            <StatCard
              title="Enqueued"
              count={overview.enqueued}
              icon={<ClockIcon size={IconSize.xl} />}
              color="blue"
              isActive={activeTab === HangfireJobState.Enqueued}
              onClick={() => handleTabChange(HangfireJobState.Enqueued)}
              mode="filter"
              tooltip="View enqueued jobs"
            />
            <StatCard
              title="Processing"
              count={overview.processing}
              icon={<PlayIcon size={IconSize.xl} />}
              color="cyan"
              isActive={activeTab === HangfireJobState.Processing}
              onClick={() => handleTabChange(HangfireJobState.Processing)}
              mode="filter"
              tooltip="View processing jobs"
            />
            <StatCard
              title="Scheduled"
              count={overview.scheduled}
              icon={<ClockIcon size={IconSize.xl} />}
              color="orange"
              isActive={activeTab === HangfireJobState.Scheduled}
              onClick={() => handleTabChange(HangfireJobState.Scheduled)}
              mode="filter"
              tooltip="View scheduled jobs"
            />
            <StatCard
              title="Succeeded"
              count={overview.succeeded}
              icon={<CheckIcon size={IconSize.xl} />}
              color="green"
              isActive={activeTab === HangfireJobState.Succeeded}
              onClick={() => handleTabChange(HangfireJobState.Succeeded)}
              mode="filter"
              tooltip="View succeeded jobs"
            />
            <StatCard
              title="Failed"
              count={overview.failed}
              icon={<WarningIcon size={IconSize.xl} />}
              color="red"
              isActive={activeTab === HangfireJobState.Failed}
              onClick={() => handleTabChange(HangfireJobState.Failed)}
              mode="filter"
              tooltip="View failed jobs"
            />
            <StatCard
              title="Deleted"
              count={overview.deleted}
              icon={<CloseIcon size={IconSize.xl} />}
              color="gray"
              isActive={activeTab === HangfireJobState.Deleted}
              onClick={() => handleTabChange(HangfireJobState.Deleted)}
              mode="filter"
              tooltip="View deleted jobs"
            />
          </SimpleGrid>
        ) : null}

        {/* Queues */}
        {overview && overview.queues && overview.queues.length > 0 && (
          <SectionCard
            title="Queues"
            icon={<IconListDetails size={IconSize.sm} />}>
            <Box p="md">
              <Table.ScrollContainer minWidth={360}>
                <Table striped highlightOnHover withTableBorder={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Queue</Table.Th>
                      <Table.Th>Enqueued</Table.Th>
                      <Table.Th>Fetched</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {overview.queues.map(q => (
                      <Table.Tr key={q.queue}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {q.queue}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{q.enqueued}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{q.fetched}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Box>
          </SectionCard>
        )}

        {/* Servers */}
        <SectionCard title="Servers" icon={<IconServer size={IconSize.sm} />}>
          {serversLoading ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : servers.length === 0 ? (
            <Center py="xl">
              <Stack align="center" gap="sm">
                <ThemeIcon size={48} radius="xl" color="gray" variant="light">
                  <IconServer size={24} />
                </ThemeIcon>
                <Text c="dimmed" size="sm">
                  No active workers
                </Text>
              </Stack>
            </Center>
          ) : (
            <Box p="md">
              <Table.ScrollContainer minWidth={640}>
                <Table striped highlightOnHover withTableBorder={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Server</Table.Th>
                      <Table.Th>Workers</Table.Th>
                      <Table.Th>Queues</Table.Th>
                      <Table.Th>Started</Table.Th>
                      <Table.Th>Heartbeat</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {servers.map(s => (
                      <Table.Tr key={s.name}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {s.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{s.workers}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{s.queues?.join(', ') ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatRelative(s.startedAt)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatRelative(s.heartbeat)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Box>
          )}
        </SectionCard>

        {/* History chart */}
        {chartData.length > 0 && (
          <SectionCard
            title="Hourly History"
            icon={<IconListDetails size={IconSize.sm} />}>
            <Box p="md">
              <AreaChart
                h={200}
                data={chartData}
                dataKey="hour"
                series={[
                  {name: 'Succeeded', color: 'green.6'},
                  {name: 'Failed', color: 'red.6'},
                ]}
                curveType="linear"
                withLegend
                withDots={false}
              />
            </Box>
          </SectionCard>
        )}

        {/* Jobs by state */}
        <SectionCard title="Jobs" icon={<IconListDetails size={IconSize.sm} />}>
          <Box>
            <Tabs
              value={String(activeTab)}
              onChange={val => {
                if (val !== null) {
                  handleTabChange(Number(val) as HangfireJobState);
                }
              }}>
              <Tabs.List px="md" pt="xs">
                {Object.values(HangfireJobState)
                  .filter(v => typeof v === 'number')
                  .map(stateVal => {
                    const state = stateVal as HangfireJobState;
                    const info = JOB_STATE_INFO[state];
                    return (
                      <Tabs.Tab key={state} value={String(state)}>
                        {info.label}
                      </Tabs.Tab>
                    );
                  })}
              </Tabs.List>

              {Object.values(HangfireJobState)
                .filter(v => typeof v === 'number')
                .map(stateVal => {
                  const state = stateVal as HangfireJobState;
                  return (
                    <Tabs.Panel key={state} value={String(state)}>
                      <Box p="md">
                        {jobsLoading ? (
                          <Center py="xl">
                            <Loader size="sm" />
                          </Center>
                        ) : !jobs || (jobs.data ?? []).length === 0 ? (
                          <Center py="xl">
                            <Text c="dimmed" size="sm">
                              No {JOB_STATE_INFO[state].label.toLowerCase()}{' '}
                              jobs
                            </Text>
                          </Center>
                        ) : (
                          <Stack gap="xs">
                            <Table.ScrollContainer minWidth={640}>
                              <Table
                                striped
                                highlightOnHover
                                withTableBorder={false}>
                                <Table.Thead>
                                  <Table.Tr>
                                    <Table.Th>ID</Table.Th>
                                    <Table.Th>Method</Table.Th>
                                    <Table.Th>Time</Table.Th>
                                    {state === HangfireJobState.Failed && (
                                      <Table.Th>Error</Table.Th>
                                    )}
                                    <Table.Th />
                                  </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                  {(jobs.data ?? []).map(
                                    (job: HangfireJobSummary) => (
                                      <Table.Tr key={job.id}>
                                        <Table.Td>
                                          <Anchor
                                            size="sm"
                                            onClick={() => openJobDetail(job.id)}>
                                            {job.id.slice(0, 8)}
                                          </Anchor>
                                        </Table.Td>
                                        <Table.Td>
                                          <Text size="sm" lineClamp={1} maw={300}>
                                            {job.method}
                                          </Text>
                                        </Table.Td>
                                        <Table.Td>
                                          <Text size="sm" c="dimmed">
                                            {jobTimestamp(job)}
                                          </Text>
                                        </Table.Td>
                                        {state === HangfireJobState.Failed && (
                                          <Table.Td>
                                            <Text
                                              size="xs"
                                              c="red"
                                              lineClamp={1}
                                              maw={250}>
                                              {job.exceptionMessage}
                                            </Text>
                                          </Table.Td>
                                        )}
                                        <Table.Td>
                                          <Menu
                                            shadow="md"
                                            width={160}
                                            position="bottom-end">
                                            <Menu.Target>
                                              <Button
                                                size="compact-xs"
                                                variant="subtle"
                                                color="gray"
                                                loading={
                                                  mutatingJobId === job.id
                                                }>
                                                Actions
                                              </Button>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                              <Menu.Item
                                                leftSection={
                                                  <IconListDetails size={14} />
                                                }
                                                onClick={() =>
                                                  openJobDetail(job.id)
                                                }>
                                                View Details
                                              </Menu.Item>
                                              {canRequeue(state) && (
                                                <Menu.Item
                                                  leftSection={
                                                    <IconRepeat size={14} />
                                                  }
                                                  onClick={() =>
                                                    handleRequeue(job.id)
                                                  }>
                                                  Requeue
                                                </Menu.Item>
                                              )}
                                              {canDelete(state) && (
                                                <Menu.Item
                                                  color="red"
                                                  leftSection={
                                                    <IconTrash size={14} />
                                                  }
                                                  onClick={() =>
                                                    handleDeleteJob(job.id)
                                                  }>
                                                  Delete
                                                </Menu.Item>
                                              )}
                                            </Menu.Dropdown>
                                          </Menu>
                                        </Table.Td>
                                      </Table.Tr>
                                    )
                                  )}
                                </Table.Tbody>
                              </Table>
                            </Table.ScrollContainer>

                            {/* Simple pagination */}
                            {jobs.totalRecords > PAGE_SIZE && (
                              <Group justify="center" mt="sm">
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  disabled={jobsPage <= 1}
                                  onClick={() => setJobsPage(jobsPage - 1)}>
                                  Previous
                                </Button>
                                <Text size="sm" c="dimmed">
                                  Page {jobsPage} of{' '}
                                  {Math.ceil(jobs.totalRecords / PAGE_SIZE)}
                                </Text>
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  disabled={
                                    jobsPage >=
                                    Math.ceil(jobs.totalRecords / PAGE_SIZE)
                                  }
                                  onClick={() => setJobsPage(jobsPage + 1)}>
                                  Next
                                </Button>
                              </Group>
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Tabs.Panel>
                  );
                })}
            </Tabs>
          </Box>
        </SectionCard>

        {/* Recurring jobs */}
        <SectionCard
          title="Recurring Jobs"
          icon={<IconRepeat size={IconSize.sm} />}>
          {recurringLoading ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : recurring.length === 0 ? (
            <Center py="xl">
              <Text c="dimmed" size="sm">
                No recurring jobs configured
              </Text>
            </Center>
          ) : (
            <Box p="md">
              <Table.ScrollContainer minWidth={820}>
                <Table striped highlightOnHover withTableBorder={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>Cron</Table.Th>
                      <Table.Th>Queue</Table.Th>
                      <Table.Th>Last State</Table.Th>
                      <Table.Th>Last Run</Table.Th>
                      <Table.Th>Next Run</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {recurring.map((job: HangfireRecurringJobResponse) => (
                      <Table.Tr key={job.id}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {job.id}
                          </Text>
                          {job.error && (
                            <Text size="xs" c="red">
                              {job.error}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Code style={{whiteSpace: 'nowrap'}}>{job.cron}</Code>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{job.queue}</Text>
                        </Table.Td>
                        <Table.Td>
                          {job.lastJobState ? (
                            <Badge
                              size="sm"
                              color={hangfireStateColor(job.lastJobState)}
                              variant="light">
                              {job.lastJobState}
                            </Badge>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {formatRelative(job.lastExecution)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {job.nextExecution
                              ? formatDate(job.nextExecution)
                              : '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            leftSection={<PlayIcon size={IconSize.sm} />}
                            loading={triggeringId === job.id}
                            onClick={() => handleTrigger(job.id)}>
                            Trigger
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Box>
          )}
        </SectionCard>
      </Stack>

      {/* Job detail drawer */}
      <Drawer
        opened={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedJobId(null);
        }}
        title={
          <Text fw={600}>
            Job Detail{selectedJobId ? ` — ${selectedJobId.slice(0, 8)}` : ''}
          </Text>
        }
        position="right"
        size="lg">
        {detailLoading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : jobDetail ? (
          <Stack gap="md">
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Method
              </Text>
              <Text size="sm">{jobDetail.method}</Text>
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                State
              </Text>
              <Badge
                color={hangfireStateColor(jobDetail.state)}
                variant="light">
                {jobDetail.state}
              </Badge>
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Created At
              </Text>
              <Text size="sm">{formatDate(jobDetail.createdAt)}</Text>
            </Box>

            {jobDetail.arguments && (
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                  Arguments
                </Text>
                <Code block style={{whiteSpace: 'pre-wrap', fontSize: 12}}>
                  {tryParseJson(jobDetail.arguments)}
                </Code>
              </Box>
            )}

            {Object.keys(jobDetail.parameters ?? {}).length > 0 && (
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                  Parameters
                </Text>
                <Table withTableBorder striped>
                  <Table.Tbody>
                    {Object.entries(jobDetail.parameters).map(([k, v]) => (
                      <Table.Tr key={k}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {k}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{v}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            )}

            {jobDetail.exceptionMessage && (
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                  Exception
                </Text>
                <Text size="sm" c="red">
                  {jobDetail.exceptionMessage}
                </Text>
                {jobDetail.exceptionDetails && (
                  <>
                    <Anchor
                      size="xs"
                      mt={4}
                      onClick={() => setExceptionExpanded(v => !v)}>
                      {exceptionExpanded ? 'Hide details' : 'Show details'}
                    </Anchor>
                    <Collapse in={exceptionExpanded}>
                      <Code
                        block
                        mt="xs"
                        style={{whiteSpace: 'pre-wrap', fontSize: 11}}>
                        {jobDetail.exceptionDetails}
                      </Code>
                    </Collapse>
                  </>
                )}
              </Box>
            )}

            {jobDetail.history && jobDetail.history.length > 0 && (
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={8}>
                  State History
                </Text>
                <Timeline bulletSize={18} lineWidth={2}>
                  {jobDetail.history.map((entry, i) => (
                    <Timeline.Item
                      key={i}
                      bullet={<IconCircleDot size={10} />}
                      title={
                        <Badge
                          size="sm"
                          color={hangfireStateColor(entry.stateName)}
                          variant="light">
                          {entry.stateName}
                        </Badge>
                      }>
                      <Text size="xs" c="dimmed">
                        {formatDate(entry.createdAt)}
                      </Text>
                      {Object.keys(entry.data ?? {}).length > 0 && (
                        <Stack gap={2} mt={4}>
                          {Object.entries(entry.data).map(([k, v]) => (
                            <Text key={k} size="xs" c="dimmed">
                              {k}: {v}
                            </Text>
                          ))}
                        </Stack>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Box>
            )}
          </Stack>
        ) : null}
      </Drawer>
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.AdminHangfireMonitor(),
  element: <HangfireMonitorPage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
